import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const HCS_LOGO_URL = 'https://customer-assets.emergentagent.com/job_203c254d-590f-4582-b077-890a39b58283/artifacts/vvnnctky_HCS%20Logo.jpg';

const ForgotPassword = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send reset email');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FAFAF9]" data-testid="forgot-password-success">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="flex justify-end mb-4">
              <LanguageSelector />
            </div>
            
            <div className="flex items-center justify-center mb-8">
              <img 
                src={HCS_LOGO_URL} 
                alt="HCS Logo" 
                className="h-16 w-auto object-contain"
              />
              <span className="ml-3 text-2xl font-bold text-[#0F4C81] font-['Nunito']">ECD-AI Connect</span>
            </div>

            <div className="bg-white rounded-2xl border-2 border-slate-100 p-8 shadow-sm text-center">
              <div className="w-16 h-16 bg-[#10B981] rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-white" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-[#111827] mb-4 font-['Nunito']">
                {t('auth.checkEmail')}
              </h2>
              <p className="text-[#4B5563] mb-6">
                {t('auth.resetEmailSent')}
              </p>
              <p className="text-sm text-[#9CA3AF] mb-6">
                {email}
              </p>
              <Link
                to="/"
                className="inline-block px-6 py-3 bg-[#0F4C81] text-white rounded-full font-bold hover:bg-[#0d3d6b] transition-all"
              >
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
        
        <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
          <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAF9]" data-testid="forgot-password-page">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-4">
            <LanguageSelector />
          </div>
          
          <div className="flex items-center justify-center mb-8">
            <img 
              src={HCS_LOGO_URL} 
              alt="HCS Logo" 
              className="h-16 w-auto object-contain"
            />
            <span className="ml-3 text-2xl font-bold text-[#0F4C81] font-['Nunito']">ECD-AI Connect</span>
          </div>

          <div className="bg-white rounded-2xl border-2 border-slate-100 p-8 shadow-sm">
            <Link
              to="/"
              className="flex items-center space-x-2 text-[#0F4C81] font-semibold mb-6 hover:underline"
            >
              <ArrowLeft size={20} />
              <span>{t('auth.backToLogin')}</span>
            </Link>

            <div className="w-14 h-14 bg-[#3B82F6] rounded-full flex items-center justify-center mb-6">
              <Mail className="text-white" size={28} />
            </div>

            <h2 className="text-3xl font-bold text-[#111827] mb-2 font-['Nunito']">
              {t('auth.forgotPassword')}
            </h2>
            <p className="text-[#4B5563] mb-6">
              {t('auth.forgotPasswordDescription')}
            </p>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">
                  {t('auth.email')}
                </label>
                <input
                  type="email"
                  data-testid="email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.enterEmail')}
                  className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                  required
                />
              </div>

              <button
                type="submit"
                data-testid="submit-button"
                disabled={loading}
                className="w-full rounded-full px-8 py-4 text-xl font-bold shadow-md hover:shadow-lg transition-all active:scale-95 bg-[#0F4C81] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('common.pleaseWait') : t('auth.sendResetLink')}
              </button>
            </form>
          </div>
        </div>
      </div>
      
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default ForgotPassword;
